/**
 * DemoModeBanner — shown on every authenticated page while a demo session is
 * active. For the shareable user/borrower demos (/try-demo and /bank-demo) it
 * surfaces a persistent "Seen enough? Sign up now" CTA that drops the visitor
 * into the signup funnel. For the time-boxed /try-demo it also counts down and
 * resets when time is up. The lender demo is excluded (lenders don't sign up).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Clock, LogOut, ArrowRight, Sparkles } from 'lucide-react';

const GREEN = '#5c9e33';

function readSession() {
  try {
    const raw = localStorage.getItem('demo_session');
    if (!raw) return null;
    // try-demo stores a JSON object; lender demo stores the string 'lender'.
    if (raw === 'lender') return { kind: 'lender' };
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export default function DemoModeBanner() {
  const location = useLocation();
  const [session, setSession] = useState(readSession);
  const [user, setUser] = useState(readUser);
  const [remaining, setRemaining] = useState(0);

  // Re-read on route change (banner mounts at app root; demo session is set
  // after the visitor launches the demo and navigates in).
  useEffect(() => {
    setSession(readSession());
    setUser(readUser());
  }, [location.pathname]);

  const endDemo = useCallback(() => {
    localStorage.removeItem('demo_session');
    localStorage.removeItem('user');
    setSession(null);
    window.location.replace('/try-demo?expired=1');
  }, []);

  // Post-demo conversion hand-off — clear the demo session and push into the
  // signup funnel (30-day trial + DEMO30), prefilled from the captured lead.
  const goSignup = useCallback(() => {
    let lead = {};
    try { lead = JSON.parse(localStorage.getItem('demo_lead') || '{}'); } catch { lead = {}; }
    const qs = new URLSearchParams({ from: 'demo', discount: 'DEMO30' });
    if (lead.email) qs.set('email', lead.email);
    if (lead.name) qs.set('name', lead.name);
    try {
      localStorage.removeItem('demo_session');
      localStorage.removeItem('user');
      localStorage.removeItem('token');
    } catch { /* ignore */ }
    window.location.href = `/signup?${qs.toString()}`;
  }, []);

  useEffect(() => {
    if (!session?.expires_at) return;
    const tick = () => {
      const ms = new Date(session.expires_at).getTime() - Date.now();
      if (ms <= 0) { endDemo(); return; }
      setRemaining(Math.floor(ms / 1000));
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [session, endDemo]);

  // Classify the current session.
  const isLenderDemo = session?.kind === 'lender' || (user?.account_type === 'lender');
  const isTimedDemo = !!session?.expires_at; // /try-demo
  const isBankDemo = !isLenderDemo && (user?.id || '').startsWith('demo-'); // /bank-demo seeded account
  const isUserDemo = !isLenderDemo && (isTimedDemo || isBankDemo);

  if (!isUserDemo) return null;

  const mm = String(Math.floor(remaining / 60)).padStart(2, '0');
  const ss = String(remaining % 60).padStart(2, '0');

  return (
    <div
      className="w-full text-white text-sm flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5 px-4 py-2"
      style={{ background: GREEN }}
      data-testid="demo-mode-banner"
    >
      <span className="inline-flex items-center gap-1.5 font-semibold">
        <Clock className="w-4 h-4" /> Demo mode
      </span>
      {isTimedDemo && (
        <span className="opacity-90">· resets in <b data-testid="demo-countdown">{mm}:{ss}</b></span>
      )}
      <button
        onClick={goSignup}
        className="ml-1 inline-flex items-center gap-1.5 bg-white text-[#2f5216] hover:bg-white/90 rounded-md px-3 py-1 text-xs font-extrabold transition shadow-sm"
        data-testid="demo-signup-now-btn"
      >
        <Sparkles className="w-3.5 h-3.5" /> Seen enough? Sign up now <ArrowRight className="w-3.5 h-3.5" />
      </button>
      <button
        onClick={endDemo}
        className="inline-flex items-center gap-1.5 bg-white/20 hover:bg-white/30 rounded-md px-2.5 py-1 text-xs font-semibold transition"
        data-testid="demo-exit-btn"
      >
        <LogOut className="w-3.5 h-3.5" /> Exit demo
      </button>
    </div>
  );
}
